/**
 * Gerador pseudoaleatório determinístico e as distribuições usadas pelos dados
 * sintéticos.
 *
 * Determinismo é requisito, não conveniência: um conjunto que muda a cada
 * carregamento tornaria a avaliação do protótipo irreprodutível — dois
 * avaliadores veriam telas diferentes, e o retorno de um não seria verificável
 * pelo outro.
 */

/** Fonte pseudoaleatória com semente, reprodutível entre execuções. */
export class SeededRandom {
  /** Estado interno de 32 bits, avançado a cada extração. */
  private state: number;

  /** Valor normal já gerado e ainda não consumido (Box-Muller produz aos pares). */
  private spareNormal: number | null = null;

  constructor(seed: number) {
    // Semente zero deixaria o gerador preso em zero; deslocamos para um valor
    // arbitrário porém fixo, preservando o determinismo.
    this.state = seed === 0 ? 0x9e3779b9 : seed >>> 0;
  }

  /**
   * Extrai o próximo valor em [0, 1).
   *
   * Algoritmo mulberry32: período de 2^32, qualidade suficiente para dados de
   * apresentação e implementável em poucas linhas — o que importa aqui é ser
   * reprodutível, não criptograficamente forte.
   */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Valor uniforme no intervalo [min, max). */
  uniform(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Inteiro uniforme no intervalo [min, max], com ambos os extremos incluídos. */
  int(min: number, max: number): number {
    return Math.floor(this.uniform(min, max + 1));
  }

  /**
   * Valor de distribuição normal, por transformação de Box-Muller.
   *
   * O método produz dois valores independentes por vez; o excedente é guardado
   * para a chamada seguinte, em vez de descartado.
   */
  normal(mean: number, stdDev: number): number {
    if (this.spareNormal !== null) {
      const spare = this.spareNormal;
      this.spareNormal = null;
      return mean + stdDev * spare;
    }
    // O logaritmo diverge em zero, então reamostramos até obter valor positivo.
    let u = 0;
    while (u === 0) {
      u = this.next();
    }
    const v = this.next();
    const magnitude = Math.sqrt(-2 * Math.log(u));
    const angle = 2 * Math.PI * v;
    this.spareNormal = magnitude * Math.sin(angle);
    return mean + stdDev * magnitude * Math.cos(angle);
  }

  /**
   * Valor de distribuição log-normal, calibrado pela mediana desejada.
   *
   * Esta é a distribuição de latência de inferência: a maioria das requisições
   * fica próxima da mediana e algumas demoram bem mais — **cauda à direita**.
   *
   * Gerar distribuição simétrica faria a diferença entre mediana e percentil 95
   * parecer pequena, e é justamente essa diferença que sustenta a regra de nunca
   * apresentar média como métrica de latência.
   *
   * @param median   valor central desejado, na mesma unidade da saída
   * @param sigma    dispersão em escala logarítmica; quanto maior, mais pesada a cauda
   */
  logNormal(median: number, sigma: number): number {
    // Numa log-normal, a mediana é exp(mu) — então mu = ln(mediana).
    return Math.exp(Math.log(median) + sigma * this.normal(0, 1));
  }

  /** Escolhe um elemento da lista. Lança quando a lista está vazia. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new Error("pick: lista vazia");
    }
    const index = this.int(0, items.length - 1);
    // O acesso é seguro por construção, mas o compilador exige a verificação
    // por causa de `noUncheckedIndexedAccess`.
    const chosen = items[index];
    if (chosen === undefined) {
      throw new Error("pick: índice fora do intervalo");
    }
    return chosen;
  }

  /** Verdadeiro com a probabilidade informada, entre 0 e 1. */
  bool(probability: number): boolean {
    return this.next() < probability;
  }
}

/**
 * Percentil por interpolação linear entre os vizinhos.
 *
 * Existe aqui para que os dados sintéticos possam ser conferidos com a mesma
 * definição que o domínio usará. Devolve `null` para coleção vazia — nunca
 * zero, que seria lido como latência excelente.
 */
export function percentile(values: readonly number[], p: number): number | null {
  if (values.length === 0 || p < 0 || p > 100) {
    return null;
  }
  // Ordena sobre cópia: reordenar a coleção recebida produziria defeito difícil
  // de rastrear em quem dependesse da ordem original.
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) {
    return sorted[0] ?? null;
  }
  const rank = (p / 100) * (sorted.length - 1);
  const lowerIndex = Math.floor(rank);
  const upperIndex = Math.ceil(rank);
  const lower = sorted[lowerIndex];
  const upper = sorted[upperIndex];
  if (lower === undefined || upper === undefined) {
    return null;
  }
  if (lowerIndex === upperIndex) {
    return lower;
  }
  return lower + (upper - lower) * (rank - lowerIndex);
}
